import { generateApp } from "@powerhousedao/codegen";
import type {
  AppModuleGlobalState,
  AppModulePHState,
} from "@powerhousedao/vetra/document-models/app-module";
import { kebabCase } from "change-case";
import { logger } from "../../logger.js";
import { BaseDocumentGen } from "../base-document-gen.js";
import type { CodegenInput } from "../types.js";
import { minimalBackupDocument } from "./utils.js";

/**
 * Generator for app documents
 */
export class AppGenerator extends BaseDocumentGen {
  readonly supportedDocumentTypes = "powerhouse/app";

  /**
   * Parse and extract the global state from the serialized state string
   */
  private extractGlobalState(
    input: CodegenInput,
  ): AppModuleGlobalState | undefined {
    if (!input.state) {
      return undefined;
    }
    const fullState = input.state as AppModulePHState;
    return fullState.global;
  }

  /**
   * Validate if this app strand should be processed
   */
  shouldProcess(input: CodegenInput): boolean {
    // First run base validation
    if (!super.shouldProcess(input)) {
      return false;
    }

    const state = this.extractGlobalState(input);
    if (!state) {
      logger.debug(`No state found for app: ${input.documentId}`);
      return false;
    }

    // Check if we have a valid app name and it's confirmed
    if (!state.name) {
      logger.debug(`No name found for app: ${input.documentId}`);
      return false;
    }

    if (state.status !== "CONFIRMED") {
      logger.debug(
        `App not confirmed: ${state.name} (status: ${state.status})`,
      );
      return false;
    }

    return true;
  }

  async generate(input: CodegenInput): Promise<void> {
    const state = this.extractGlobalState(input);
    if (!state) {
      logger.error(`No state found for app: ${input.documentId}`);
      return;
    }

    // Check if we have a valid app name and it's confirmed
    if (state.name && state.status === "CONFIRMED") {
      logger.info(`🔄 Starting app generation for app: ${state.name}`);
      try {
        // Generate app ID using kebabCase
        const appId: string = kebabCase(state.name);
        // Generate the app using the codegen function
        await generateApp(
          {
            ...this.config.PH_CONFIG,
            appName: state.name,
            appId: appId,
            allowedDocumentTypes: state.allowedDocumentTypes ?? [],
            isDragAndDropEnabled: state.isDragAndDropEnabled,
          },
          this.project,
        );
        await this.project.save();

        logger.info(
          `✅ App generation completed successfully for app: ${state.name}`,
        );

        // Backup the document
        const fullState = input.state as AppModulePHState;
        await minimalBackupDocument(
          {
            documentId: input.documentId,
            documentType: input.documentType,
            branch: input.branch,
            state: fullState,
            name: state.name,
          },
          this.config.CURRENT_WORKING_DIR,
        );
      } catch (error) {
        logger.error(
          `❌ Error during app generation for app ${state.name}:`,
          error,
        );
        if (error instanceof Error) {
          logger.error(`❌ Error message: ${error.message}`);
        }
      }
    } else {
      if (!state.name) {
        logger.error(`❌ Skipping app generation - missing name for app`);
        return;
      } else if (state.status !== "CONFIRMED") {
        logger.error(
          `❌ Skipping app generation - app "${state.name}" is not confirmed (status: ${state.status})`,
        );
        return;
      }
    }
  }
}
